





















































































































import { mapGetters, mapState } from 'vuex'
import DealerSiteLayoutService from '~/services/dealers/site/DealerSiteLayoutService'
import {
  CLASSIFIED_SEARCH_NS,
  SearchState
} from '~/store/modules/shared/classifieds/search/state'
import { DEALER_SITE_NS } from '~/store/modules/shared/dealers/site/state'
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  watch
} from '~/utils/nuxt3-migration'
import { BackendMetaItem, MetaItem } from '~/models/head/types'
import ClassifiedSearchShimmer from '~/components/car/classifieds/search/ClassifiedSearchShimmer.vue'
import ClassifiedSearchResults from '~/components/car/classifieds/search/results/ClassifiedSearchResults.vue'
import CBreadcrumbs from '~/components/shared/configurable/CBreadcrumbs.vue'
import ClassifiedSearchFacetsFloatingButton from '~/components/car/classifieds/search/ClassifiedSearchFacetsFloatingButton.vue'
import ConsentPrompt from '~/components/shared/consent/ConsentPrompt.vue'
import { useNamespacedStore } from '~/compositions/store'
import { PAGE_NS, PageState } from '~/store/modules/shared/page/state'
import { useUserAgent } from '~/compositions/user-agent'
import { useDeps } from '~/compositions/dependency-container'
import LegacySearchService from '~/services/search/LegacySearchService'
import {
  SET_LOADING_RESULTS,
  SET_PARAMS,
  SET_SCROLL_TOP
} from '~/store/modules/shared/classifieds/search/mutation-types'
import { ciTrash } from '~/icons/source/regular/trash'
import LazyHydrate from 'vue-lazy-hydration'
import { Facet } from '~/models/search/types'
import ClassifiedSearchFacetsComponentsService from '~/services/search/ClassifiedSearchFacetsComponentsService'
import ClassifiedSearchCategoryPicker from '~/components/car/classifieds/search/facets/ClassifiedSearchCategoryPicker.vue'
import { formatNumber } from '~/utils/number'
import { capitalizeSentences } from '~/utils/string'
import { useI18n } from '~/compositions/i18n'

export default defineComponent({
  components: {
    ClassifiedSearchCategoryPicker,
    ClassifiedSearchFacetsFloatingButton,
    CBreadcrumbs,
    ClassifiedSearchResults,
    ClassifiedSearchShimmer,
    ConsentPrompt,
    LazyHydrate
  },
  computed: {
    ...mapGetters(DEALER_SITE_NS, ['activePage', 'componentShouldRenderPage']),
    ...mapState(DEALER_SITE_NS, {
      siteTitle: 'title',
      loading: 'loading'
    })
  },
  setup() {
    const {
      state: classifiedSearchState,
      dispatch: classifiedSearchDispatch,
      commit: classifiedSearchCommit,
      getters: classifiedSearchGetters
    } = useNamespacedStore<SearchState>(CLASSIFIED_SEARCH_NS)
    const { getters: pageGetters } = useNamespacedStore<PageState>(PAGE_NS)
    const { isMobile } = useUserAgent()
    const [
      legacySearchService,
      classifiedSearchFacetsComponentsService
    ] = useDeps(LegacySearchService, ClassifiedSearchFacetsComponentsService)
    const { tc } = useI18n()

    onMounted(async () => {
      classifiedSearchDispatch('recordListViewsStats', { debounced: false })

      window.history.replaceState(
        { isInSearchPage: true },
        '',
        window.location.href
      )

      await nextTick()
      if (scrollTop.value) {
        window.scrollTo(0, scrollTop.value)
        classifiedSearchCommit(SET_SCROLL_TOP, 0)
      }

      window.addEventListener('popstate', onPopState, false)
    })

    onBeforeUnmount(() => {
      closeMobileFacetsIfOpen()
      window.removeEventListener('popstate', onPopState)
    })

    const facets = computed(() => classifiedSearchState.facets)

    const searchId = computed(() => classifiedSearchState.searchId)

    const loadingFacets = computed(() => classifiedSearchState.loading.facets)

    const loadingResults = computed(() => classifiedSearchState.loading.results)

    const scrollTop = computed(() => classifiedSearchState.scrollTop)

    const pageIsExplicitAndRequiresConsent = computed(() =>
      pageGetters('pageIsExplicitAndRequiresConsent')
    )

    const selectedFacetsCount = computed(() =>
      classifiedSearchGetters('getFacetsCount')
    )

    const totalRows = computed(() => classifiedSearchGetters('getTotalRows'))

    const breadcrumbs = computed(() => classifiedSearchState.page?.breadcrumbs)

    const showMobileFacets = computed(
      () => classifiedSearchState.showMobileFacets
    )

    const displayedFacets = computed(() =>
      facets.value
        .map(facet => ({
          facet,
          component: getFacetComponent(facet)
        }))
        .filter(item => item.component !== null)
    )

    const resultsText = computed(() =>
      capitalizeSentences(
        tc(
          'show 1 classified | show {count} classifieds',
          formatNumber(totalRows.value)
        )
      )
    )

    watch(searchId, () => {
      classifiedSearchCommit(SET_SCROLL_TOP, 0)
    })

    function getFacetBindings(facet: Facet) {
      return legacySearchService.getFacetComponentBindings(facet)
    }

    function closeMobileFacetsIfOpen() {
      if (showMobileFacets.value) {
        classifiedSearchDispatch('toggleMobileFacets')
      }
    }

    async function onPopState(event: PopStateEvent) {
      if (!event.state) {
        return
      }

      if (!event.state.isInSearchPage) {
        // If the state is not related to the search page, we do not handle it
        return
      }

      classifiedSearchCommit(SET_LOADING_RESULTS, true)

      const { pathname, search } = event.target.location

      const params = await legacySearchService.getParams(pathname + search)

      classifiedSearchCommit(SET_PARAMS, params)

      classifiedSearchDispatch('search', {
        pageView: true,
        updateUrl: false
      })
    }

    function onClearAllButtonClick() {
      classifiedSearchDispatch('clearAll')
    }

    function getFacetComponent(facet: Facet) {
      if (!facet.visible) {
        return null
      }

      return classifiedSearchFacetsComponentsService.getFacetComponent(
        facet.type
      )
    }

    function hideMobileFacets() {
      if (showMobileFacets.value) {
        classifiedSearchDispatch('toggleMobileFacets')
      }
    }

    return {
      ciTrash,
      searchId,
      displayedFacets,
      isMobile,
      loadingFacets,
      loadingResults,
      showMobileFacets,
      breadcrumbs,
      pageIsExplicitAndRequiresConsent,
      facets,
      selectedFacetsCount,
      resultsText,
      getFacetComponent,
      getFacetBindings,
      hideMobileFacets,
      onClearAllButtonClick
    }
  },
  head() {
    const dealerSiteLayoutService = this.$dep(DealerSiteLayoutService)

    const page = this.$store.state.classifieds.search.page
    const consent = this.$store.state.page.consent
    const meta: MetaItem[] = page.metadata.map((metaItem: BackendMetaItem) => ({
      ...metaItem,
      hid: metaItem.name || metaItem.property
    }))

    if (consent?.name === 'over18') {
      meta.push(
        { name: 'rating', content: 'adult' },
        { name: 'rating', content: 'RTA-5042-1996-1400-1577-RTA' }
      )
    }

    const head = {
      title: page.title,
      meta
    }

    dealerSiteLayoutService.setPageTitle(head, head.title)

    return head
  }
})
